import { useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setEntitlements,
  setLoading,
  clearEntitlements,
  markFetchError,
} from '@/store/subscriptionSlice';
import { subscriptionApi } from '@/api/subscription';
import type { SubscriptionFeatures, TierName } from '@/types/subscription';

// If the last successful fetch is older than this AND there's been an error
// since, consider entitlements stale and show a refresh banner.
const STALE_THRESHOLD_MS = 5 * 60 * 1000;

export function useEntitlements() {
  const dispatch = useAppDispatch();
  const entitlements = useAppSelector((state) => state.subscription.entitlements);
  const isLoading = useAppSelector((state) => state.subscription.isLoading);
  const lastFetchedAt = useAppSelector((state) => state.subscription.lastFetchedAt);
  const lastFetchErrorAt = useAppSelector((state) => state.subscription.lastFetchErrorAt);
  const currentOrgId = useAppSelector((state) => state.organization.currentOrgId);
  const currentWkIdentity = useAppSelector((state) => state.auth.user?.wkIdentity);
  const prevOrgIdRef = useRef<string | null>(null);

  // Returns the freshly-fetched entitlements on success, or null on failure.
  // Callers that need to observe the new state synchronously (e.g. the
  // post-checkout poll loop) can read the return value directly instead
  // of waiting for a React re-render to propagate into a ref.
  const fetchEntitlements = useCallback(async () => {
    if (!currentOrgId) {
      dispatch(clearEntitlements());
      return null;
    }
    dispatch(setLoading(true));
    try {
      const data = await subscriptionApi.getEntitlements(currentOrgId);
      dispatch(setEntitlements(data));
      return data;
    } catch (err) {
      // Discriminate permission errors from transient ones. If the server
      // rejects us (403 — user was kicked from the org) or can't find the
      // org (404), clear so the UI stops showing paid-tier affordances the
      // user no longer has. For 5xx / network / unknown, keep the last-known
      // value so a blip doesn't flip a paid org into free-tier gating —
      // but mark the error timestamp so the UI can surface a stale banner
      // if we've been unable to refresh for a while.
      const status = (err as { status?: number } | null)?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) {
        dispatch(clearEntitlements());
      } else {
        dispatch(markFetchError());
      }
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  }, [currentOrgId, dispatch]);

  useEffect(() => {
    if (currentOrgId === prevOrgIdRef.current) return;
    prevOrgIdRef.current = currentOrgId;

    if (!currentOrgId) {
      dispatch(clearEntitlements());
      return;
    }

    // Clear existing entitlements BEFORE the fetch so the previous org's
    // tier doesn't flash into the new org's UI during the fetch window.
    // With the keep-last-known-on-error behaviour, this is the only point
    // where stale cross-org state is guaranteed to be wiped.
    dispatch(clearEntitlements());
    void fetchEntitlements();
  }, [currentOrgId, dispatch, fetchEntitlements]);

  // Multi-tab sync: when another tab changes plan / cancels / resumes, it
  // broadcasts on this channel so this tab can refresh its cached
  // entitlements without a full reload. Filter by BOTH orgId and
  // wkIdentity — BroadcastChannel is origin-scoped (not session-scoped),
  // so on a shared browser where user A and user B are both logged in
  // across tabs, an orgId-only filter would leak user B's plan-change
  // signal into user A's tab.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('ssp-subscription');
    const handle = (ev: MessageEvent) => {
      const data = ev.data as {
        type?: string;
        orgId?: string;
        wkIdentity?: string;
      } | null;
      if (data?.type !== 'entitlements-changed') return;
      if (data.orgId !== currentOrgId) return;
      // Only react when the broadcast came from the same user. Fall back
      // to orgId-only when no wkIdentity is attached (older messages).
      if (data.wkIdentity && currentWkIdentity && data.wkIdentity !== currentWkIdentity) {
        return;
      }
      void fetchEntitlements();
    };
    channel.addEventListener('message', handle);
    return () => {
      channel.removeEventListener('message', handle);
      channel.close();
    };
  }, [currentOrgId, currentWkIdentity, fetchEntitlements]);

  // Refresh entitlements when the tab regains focus. Primary use case: user
  // went to the Stripe dashboard / Customer Portal and cancelled or changed
  // their subscription there. Our webhook updates the DB, but since the
  // change didn't originate from THIS tab (and didn't broadcast), the
  // client wouldn't notice until a full navigation. Listen for `focus` +
  // `visibilitychange` so a refocused tab pulls fresh state immediately.
  useEffect(() => {
    if (!currentOrgId) return;
    const onFocus = () => {
      void fetchEntitlements();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentOrgId, fetchEntitlements]);

  const isFeatureEnabled = useCallback(
    (key: keyof SubscriptionFeatures): boolean => {
      // Default to disabled while entitlements aren't yet loaded so we don't
      // flash paid-tier features (export buttons, spam detection, etc.) to
      // a Free-tier user during the initial fetch. Callers that need to
      // render optimistically during load (e.g. FeatureGate) should branch
      // on `isLoading`/`entitlements` explicitly.
      if (!entitlements) return false;
      return entitlements.features[key];
    },
    [entitlements]
  );

  const isChainEnabled = useCallback(
    (chain: string): boolean => {
      // Solana devnet is a testnet — always free regardless of tier, and
      // available even for orgs whose stored enabledChains predates it (matches
      // the backend isChainEnabled rule).
      if (chain === 'solDevnet') return true;
      // Same default-closed rationale as isFeatureEnabled.
      if (!entitlements) return false;
      if (entitlements.enabledChains.length === 0) return true; // Empty = all enabled
      return entitlements.enabledChains.includes(chain);
    },
    [entitlements]
  );

  const isPastDue = entitlements?.status === 'past_due';
  const isFree = !entitlements || entitlements.status === 'free';

  const currentTier: TierName = entitlements?.tier ?? 'free';
  const hasStripeSubscription = !!entitlements?.stripe;

  // Use effectiveLimits (server-enforced stricter ceiling during scheduled
  // downgrade window) when available, falling back to the current tier's
  // limits otherwise. This keeps the UI's disabled states aligned with the
  // server's actual allow/deny decisions.
  const effMaxVaults = entitlements?.effectiveLimits?.maxVaults ?? entitlements?.limits.maxVaults;
  const effMaxMembers =
    entitlements?.effectiveLimits?.maxMembers ?? entitlements?.limits.maxMembers;

  const canCreateVault =
    !!entitlements && (effMaxVaults === -1 || entitlements.usage.vaults < (effMaxVaults ?? 0));

  const canAddMember =
    !!entitlements && (effMaxMembers === -1 || entitlements.usage.members < (effMaxMembers ?? 0));

  const refreshEntitlements = useCallback(async () => {
    return fetchEntitlements();
  }, [fetchEntitlements]);

  // Tell other tabs in this origin that entitlements for currentOrgId have
  // changed so they refresh without waiting for their next org-switch /
  // focus event. Called by the plan-change/cancel/resume flows after they
  // successfully refresh the local entitlements.
  const broadcastEntitlementsChanged = useCallback(() => {
    if (typeof BroadcastChannel === 'undefined' || !currentOrgId) return;
    try {
      const channel = new BroadcastChannel('ssp-subscription');
      channel.postMessage({
        type: 'entitlements-changed',
        orgId: currentOrgId,
        wkIdentity: currentWkIdentity,
      });
      channel.close();
    } catch {
      // BroadcastChannel.postMessage can throw in edge cases; swallow.
    }
  }, [currentOrgId, currentWkIdentity]);

  // Stale when: we have cached entitlements, the last successful fetch is
  // older than STALE_THRESHOLD_MS, and at least one error has occurred
  // since. Used by BillingPage to surface a refresh banner.
  const isStale =
    !!entitlements &&
    !!lastFetchErrorAt &&
    (!lastFetchedAt || Date.now() - lastFetchedAt > STALE_THRESHOLD_MS) &&
    (!lastFetchedAt || lastFetchErrorAt > lastFetchedAt);

  return {
    entitlements,
    isLoading,
    isStale,
    isFeatureEnabled,
    isChainEnabled,
    isPastDue,
    isFree,
    currentTier,
    hasStripeSubscription,
    canCreateVault,
    canAddMember,
    refreshEntitlements,
    broadcastEntitlementsChanged,
  };
}

export default useEntitlements;
